// couponService.js
// Serviço frontend para o fluxo de Coupons.
// Espelha o padrão de activityGeneralService.js.

import api from "./api";

function hasFilterValue(value) {
  return value !== null && value !== undefined && value !== "";
}

/**
 * Busca coupons de forma paginada com filtros opcionais.
 *
 * @param {number} pageNumber
 * @param {number} pageSize
 * @param {object} filters
 *   - search            {string}        – busca textual por código
 *   - status            {boolean|string} – true | false | "" (todos)
 *   - eligibleRule      {string}        – ex.: "TARGETED" | "" (todas)
 *   - applyToAllCycles  {boolean|string} – true | false | "" (todos)
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function fetchCouponsPaged(pageNumber, pageSize, filters = {}) {
  // Monta params ignorando valores vazios/nulos para não poluir a query string.
  // ✅ PATCH: status agora é enviado como isActive, alinhado ao backend.
  // ✅ PATCH: applyToAllCycles preserva false, pois false é filtro válido.
  const params = { pageNumber, pageSize };

  if (filters.search?.trim()) {
    params.search = filters.search.trim();
  }

  if (hasFilterValue(filters.status)) {
    params.isActive = filters.status;
  }

  if (filters.eligibleRule?.trim()) {
    params.eligibleRule = filters.eligibleRule.trim();
  }

  if (hasFilterValue(filters.applyToAllCycles)) {
    params.applyToAllCycles = filters.applyToAllCycles;
  }

  return api.get("/Coupons/paged", { params });
}

/**
 * Busca um coupon pelo seu ID.
 * @param {number} id
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function getCouponById(id) {
  return api.get(`/Coupons/${id}`);
}

/**
 * Cria um novo coupon.
 * @param {object} data
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function createCoupon(data) {
  return api.post("/Coupons", data);
}

/**
 * Atualiza um coupon existente.
 * @param {object} data - payload com couponId + campos editáveis
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function updateCoupon(data) {
  return api.put(`/Coupons/${data.couponId}`, data);
}

/**
 * Exclui um coupon pelo seu ID.
 * @param {number} id
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function deleteCoupon(id) {
  return api.delete(`/Coupons/${id}`);
}

/**
 * Exclui múltiplos coupons em lote.
 * Backend: DELETE /api/Coupons/batch
 *
 * @param {number[]} ids
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function deleteCouponsBatch(ids) {
  return api.delete("/Coupons/batch", { data: ids });
}

/**
 * Atualiza apenas o status (ativo/inativo) de um coupon.
 * Espelha updateActivityStatus em activityService.js.
 * @param {number} couponId
 * @param {boolean} isActive
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function updateCouponStatus(couponId, isActive) {
  // ✅ PATCH: endpoint corrigido de /active para /status.
  return api.patch(`/Coupons/${couponId}/status`, { isActive });
}
